import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ClipboardList, Save, ChevronDown, ChevronRight, Lock, Plus, Minus } from "lucide-react";
import { API } from "../../context/AuthContext";
import REGISTRY from "../../data/divorceIQMaster.json";

const inputCls = "w-full bg-gray-950 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-100 focus:outline-none focus:border-[#f59e0b]/50";
const labelCls = "text-[9px] font-mono text-gray-400 uppercase";
const CASE_STAGES = ["Intake Only", "Negotiation Prep", "Judgment Prep"];
const FORCED_ON_JUDGMENT = new Set(["Income", "Assets", "Debts", "Expenses & Needs"]);

function instanceKey(templateKey, i) {
  if (templateKey.includes("_n_")) return templateKey.replace("_n_", `_${i}_`);
  if (templateKey.endsWith("_n")) return templateKey.slice(0, -2) + `_${i}`;
  return `${templateKey}__${i}`;
}

function FieldInput({ f, value, onChange, disabled }) {
  if (f.type === "select") {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={`intake-${f.key}`} className={inputCls}>
        <option value="">— select —</option>
        {(f.options || ["Yes", "No"]).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (f.type === "textarea") {
    return <textarea value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} rows={3} data-testid={`intake-${f.key}`} className={`${inputCls} resize-y`} />;
  }
  return <input type={f.type} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={`intake-${f.key}`} className={inputCls} />;
}

function Field({ f, keyOverride, label, fields, set, canEdit }) {
  const k = keyOverride || f.key;
  if (f.showIf && (fields[f.showIf.key] || "") !== f.showIf.value) return null;
  return (
    <div className={f.type === "textarea" ? "sm:col-span-2" : ""}>
      <label className={labelCls}>{label || f.question || f.label}{f.required && <span className="text-rose-400"> *</span>}</label>
      {f.help && <p className="text-[9px] text-gray-500 mb-0.5">{f.help}</p>}
      {canEdit
        ? <FieldInput f={{ ...f, key: k }} value={fields[k] || ""} onChange={(v) => set(k, v)} disabled={!canEdit} />
        : <p className="text-xs text-gray-300 py-1">{fields[k] || <span className="text-gray-600 italic">Unknown</span>}</p>}
    </div>
  );
}

function Group({ group, fields, set, canEdit, counts, setCount }) {
  const n = counts[group.group_id] || 1;
  return (
    <div className="space-y-2 border-t border-white/5 pt-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono font-bold text-[#2dd4bf] uppercase">{group.group_id.replace(/_/g, " ")} entries</span>
        {canEdit && (
          <div className="flex items-center gap-1">
            <button onClick={() => setCount(group.group_id, Math.max(1, n - 1))} data-testid={`group-minus-${group.group_id}`} className="text-gray-400 hover:text-rose-400 border border-white/10 rounded p-1"><Minus className="w-3 h-3" /></button>
            <span className="text-[10px] font-mono text-gray-300 w-6 text-center">{n}</span>
            <button onClick={() => setCount(group.group_id, Math.min(group.max, n + 1))} data-testid={`group-plus-${group.group_id}`} className="text-gray-400 hover:text-[#2dd4bf] border border-white/10 rounded p-1"><Plus className="w-3 h-3" /></button>
          </div>
        )}
      </div>
      {Array.from({ length: n }, (_, idx) => {
        const i = idx + 1;
        return (
          <div key={i} className="border border-white/10 rounded-xl p-3 space-y-2" data-testid={`intake-instance-${group.group_id}-${i}`}>
            <p className="text-[10px] font-mono font-bold text-gray-400 uppercase">{group.group_id.replace(/_/g, " ")} #{i}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {group.fields.map((f) => (
                <Field key={`${f.key}_${i}`} f={f} keyOverride={instanceKey(f.key, i)} label={(f.question || f.label).replace(/#/g, i)} fields={fields} set={set} canEdit={canEdit} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Module({ mod, fields, set, canEdit, forced, counts, setCount }) {
  const [open, setOpen] = useState(false);
  const gateOn = !mod.gate || ["Yes"].includes(fields[mod.gate.key]) || forced;
  const totalSlots = mod.fields.length + mod.groups.reduce((n, gr) => n + gr.fields.length, 0);
  const answered = Object.keys(fields).filter((k) => String(fields[k]).trim() && (mod.fields.some((f) => f.key === k) || mod.groups.some((gr) => gr.fields.some((f) => k.startsWith(f.key.replace(/_n_.*|_n$/, "")))))).length;

  return (
    <div className="bg-[#131f3a] border border-white/5 rounded-2xl overflow-hidden" data-testid={`intake-module-${mod.id}`}>
      <button onClick={() => setOpen(!open)} data-testid={`intake-module-toggle-${mod.id}`} className="w-full flex items-center justify-between gap-2 p-4 hover:bg-white/5 transition-colors duration-200">
        <span className="text-xs font-bold text-white uppercase tracking-wide font-display flex items-center gap-2">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-[#f59e0b]" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-500" />}
          {mod.title}
          {mod.gate && !gateOn && <Lock className="w-3 h-3 text-gray-600" />}
        </span>
        <span className="text-[9px] font-mono text-gray-500">{totalSlots} fields</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {mod.infos.map((inf, i) => (
            <p key={i} className="text-[10px] text-gray-400 bg-gray-950 border border-white/5 rounded-lg p-2.5 leading-relaxed"><span className="font-bold text-gray-300">{inf.label}: </span>{inf.text}</p>
          ))}
          {mod.gate && (
            <div className="border border-[#f59e0b]/20 rounded-xl p-3 bg-[#f59e0b]/5">
              <Field f={mod.gate} fields={fields} set={set} canEdit={canEdit} />
              {forced && <p className="text-[9px] text-[#f59e0b] font-mono mt-1">FORCED VISIBLE — Judgment Prep stage requires this module.</p>}
            </div>
          )}
          {gateOn && (
            <>
              {mod.fields.length > 0 && (
                <div className="grid sm:grid-cols-2 gap-3">
                  {mod.fields.map((f) => <Field key={f.key} f={f} fields={fields} set={set} canEdit={canEdit} />)}
                </div>
              )}
              {mod.groups.map((gr) => <Group key={gr.group_id} group={gr} fields={fields} set={set} canEdit={canEdit} counts={counts} setCount={setCount} />)}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function IntakePanel({ caseId, canEdit }) {
  const [fields, setFields] = useState({});
  const [counts, setCounts] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    axios.get(`${API}/intake`, { params: { case_id: caseId } })
      .then((res) => {
        const f = res.data.fields || {};
        setFields(f);
        const c = {};
        REGISTRY.modules.forEach((m) => m.groups.forEach((gr) => {
          let maxSeen = 1;
          for (let i = gr.max; i >= 1; i--) {
            if (gr.fields.some((fd) => String(f[instanceKey(fd.key, i)] || "").trim())) { maxSeen = i; break; }
          }
          c[gr.group_id] = maxSeen;
        }));
        setCounts(c);
      })
      .catch(() => toast.error("Couldn't load registry data."))
      .finally(() => setLoaded(true));
  }, [caseId]);

  const set = (key, val) => setFields((f) => ({ ...f, [key]: val }));
  const setCount = (gid, n) => setCounts((c) => ({ ...c, [gid]: n }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/intake`, { case_id: caseId, fields });
      toast.success(`Registry saved — ${res.data.field_count} fields.`);
    } catch {
      toast.error("Couldn't save registry.");
    } finally {
      setSaving(false);
    }
  };

  const answered = Object.values(fields).filter((v) => String(v).trim()).length;
  const judgmentPrep = fields.IQ_case_stage === "Judgment Prep";

  if (!loaded) return <div className="text-xs text-[#f59e0b] font-mono animate-blink py-12 text-center">Loading Divorce IQ data registry...</div>;

  const SaveBtn = ({ testid }) => canEdit && (
    <button onClick={save} disabled={saving} data-testid={testid} className="w-full flex items-center justify-center gap-1.5 bg-[#f59e0b] hover:bg-[#fbbf24] disabled:opacity-60 text-gray-950 font-bold text-xs font-display uppercase tracking-wider py-2.5 rounded-xl transition-colors duration-200">
      <Save className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save Registry"}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="intake-panel">
      <div className="bg-[#131f3a] border border-white/5 rounded-2xl p-5 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-bold text-white uppercase tracking-wide font-display flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-[#f59e0b]" /> Divorce IQ Data Registry
          </h3>
          <span className="text-[10px] font-mono text-gray-400" data-testid="intake-progress">{answered} fields captured</span>
        </div>
        <p className="text-[10px] text-gray-400 leading-relaxed">
          The complete data map Professor Flux hunts for in every uploaded document — matching fields are <span className="text-[#2dd4bf] font-bold">auto-filled from your records</span> (verbatim, never inferred). Blanks feed the Gap Audit's client outreach emails. Correct or add values manually anytime. Use + / − to add repeating entries (children, accounts, vehicles, properties).
        </p>
        <div>
          <label className={labelCls}>Case Stage / Completion Level</label>
          <select value={fields.IQ_case_stage || ""} onChange={(e) => set("IQ_case_stage", e.target.value)} data-testid="intake-IQ_case_stage" className={inputCls} disabled={!canEdit}>
            <option value="">— select —</option>
            {CASE_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {judgmentPrep && <p className="text-[9px] text-[#f59e0b] font-mono mt-1">JUDGMENT PREP: Income, Expenses, Assets & Debts modules are forced visible.</p>}
        </div>
        <SaveBtn testid="intake-save-btn" />
      </div>

      {REGISTRY.modules.map((mod) => (
        <Module key={mod.id} mod={mod} fields={fields} set={set} canEdit={canEdit} counts={counts} setCount={setCount} forced={judgmentPrep && FORCED_ON_JUDGMENT.has(mod.title)} />
      ))}

      <SaveBtn testid="intake-save-btn-bottom" />
    </div>
  );
}
